import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlaylistIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as CollectionDetailsIcon } from "../../assets/icons/collectionDetails.svg";
import { ReactComponent as CropIcon } from "../../assets/icons/crop.svg";

import input1Placeholder from "../../assets/placeholders/input1.png";
import input2Placeholder from "../../assets/placeholders/input2.png";
import groupVideoPlaceholder from "../../assets/placeholders/groupVideo.mp4";

import {
  AdminActionButton,
  AdminActionsContainer,
  CategoryContainer,
  CategoryFadeWrapper,
  CategoryItem,
  CategoryText,
  Container,
  Content,
  GenerateContainer,
  GenerateContainerMobile,
  Header,
  IconWrapper,
  ModalContainer,
  RightContent,
  RightContentOverlay,
  RowIcons,
  StyledIconWrapper,
  StyledImage,
  StyledVideo,
  Title,
} from "../GenerateCollectionModal/GenerateCollectionModal.styles";

import {
  NudifyContainer,
  NudifyText,
  SettingsContainer,
  DeleteButton,
  CropButton,
  UploadActions,
  Image,
  ImageContainer,
} from "../EditImageModal/EditImageModal.styles";

import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useScrollFade } from "../../hooks/useScrollFade";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import EnterPromptModal from "../EnterPromptModal/EnterPromptModal";
import AddToPlaylistModal from "../AddToPlaylistModal/AddToPlaylistModal";
import CreatePlaylistModal from "../CreatePlaylistModal/CreatePlaylistModal";
import CropModal from "../CropModal/CropModal";
import UserGalleryModal from "../UserGalleryModal/UserGalleryModal";
import CreateImageModal from "../CreateImageModal/CreateImageModal";
import SelectImageButton from "../SelectImageButton/SelectImageButton";
import UploadAdButton from "../PlayboxAdCard/UploadAdButton";
import LottieIcon from "../LottieIcon/LottieIcon";
import likeAnimation from "../../assets/animations/like.json";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import settingsAnimation from "../../assets/animations/settings.json";
import deleteAnimation from "../../assets/animations/trash.json";
import FlyToCollection from "../FlyToCollection/FlyToCollection";
import ExplosionButton from "../ExplosionButton/ExplosionButton";
import GradientStarIcon from "../GradientStarIcon/GradientStarIcon";
import GenerationToast from "../utils/GenerationToast";

import {
  selectGenerateCollection,
  selectHideCollectionPending,
  selectHideCollectionVipPending,
  selectSelectedCollection,
  selectToggleToFavoriteLoading,
} from "../../store/slices/model/slice";
import {
  generateBlendCollection,
  hideCollection,
  hideCollectionVip,
  toggleToFavorite,
} from "../../store/slices/model/asyncThunks";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectVideoSettings,
  triggerCollectionTabAnimation,
} from "../../store/slices/common/slice";
import {
  ALLOWED_HIDE_COLLECTION_USER_IDS,
  AUDIO_PRICE,
  MEDIA_TYPE,
  UPSCALE_PRICE,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import { formatModelName, isImageOutput } from "../../utils/utils";

const hasVipTag = (collection) =>
  Array.isArray(collection?.tags) &&
  collection.tags.some((tag) =>
    typeof tag === "string" ? tag === "VIP" : tag?.name === "VIP",
  );

const MemoizedRightContent = React.memo(({ output, input }) => {
  if (!output || !input) return null;

  return (
    <RightContent>
      {output.type === MEDIA_TYPE.VIDEO ? (
        <StyledVideo
          autoplay
          src={output.video?.compressedUrl || output.video?.url}
          imageSrc={input.image?.resizedImage?.url || input.image?.url}
        />
      ) : (
        <StyledImage src={output.image?.url} />
      )}
      <RightContentOverlay />
    </RightContent>
  );
});

const GroupModal = ({
  isOpen,
  onClose,
  playlistId,
  fromTrainingSelectCoverPage,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { username: profileUsername } = useParams();

  const user = useSelector(selectUser);
  const collection = useSelector(selectSelectedCollection);

  const loading = useSelector(selectToggleToFavoriteLoading);
  const { loading: generateLoading } = useSelector(selectGenerateCollection);
  const hideCollectionPending = useSelector(selectHideCollectionPending);
  const hideCollectionVipPending = useSelector(selectHideCollectionVipPending);
  const videoSettings = useSelector(selectVideoSettings);

  const [categoryRef, isCategoryFaded] = useScrollFade("horizontal", [
    collection?.keywords,
  ]);

  const isLoading =
    loading ||
    generateLoading ||
    hideCollectionPending ||
    hideCollectionVipPending;

  const userId = user?._id || user?.id;
  const canManageCollectionVisibility =
    !!userId && ALLOWED_HIDE_COLLECTION_USER_IDS.includes(userId);
  const isOnCollectionUserProfile =
    !!collection?.username && profileUsername === collection.username;

  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImage2, setUploadedImage2] = useState(null);
  const [upscaleEnabled, setUpscaleEnabled] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customSeed, setCustomSeed] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );

  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [cropTarget, setCropTarget] = useState(null);

  const [isUserGalleryModalOpen, setIsUserGalleryModalOpen] = useState(false);
  const [isCreateImageFromGalleryOpen, setIsCreateImageFromGalleryOpen] =
    useState(false);
  const [galleryTarget, setGalleryTarget] = useState(null);

  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [negativePrompt, setNegativePrompt] = useState("");
  const [audioPrompt, setAudioPrompt] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(
    localStorage.getItem("audioEnabled") === "true",
  );
  const [showPrompts, setShowPrompts] = useState(false);
  const [videoResolution, setVideoResolution] = useState(
    localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION,
  );
  const [fps, setFps] = useState(
    Number(localStorage.getItem("videoFps") ?? DEFAULT_VIDEO_FPS),
  );
  const [videoDuration, setVideoDuration] = useState(
    Number(localStorage.getItem("videoDuration") ?? DEFAULT_VIDEO_DURATION),
  );
  const [isAddToPlaylistModalOpen, setIsAddToPlaylistModalOpen] =
    useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isCreatePlaylistModalOpen, setIsCreatePlaylistModalOpen] =
    useState(false);
  const [playlistName, setPlaylistName] = useState("");

  const [isExploding, setIsExploding] = useState(false);
  const [isCollectionHiddenLocally, setIsCollectionHiddenLocally] =
    useState(false);
  const [isVipTagHiddenLocally, setIsVipTagHiddenLocally] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [flyAnimation, setFlyAnimation] = useState({
    isActive: false,
    imageUrl: null,
    startPosition: null,
    imageDimensions: null,
    onComplete: null,
  });

  const uploadedImageRef = useRef(null);

  useLockBodyScroll(isOpen || isEnterPromptModalOpen);

  useEffect(() => {
    setIsCollectionHiddenLocally(collection?.isPublic === false);
    setIsVipTagHiddenLocally(false);
  }, [collection?._id, collection?.isPublic]);

  useEffect(() => {
    if (!isOpen) return;
    const savedResolution = localStorage.getItem("videoResolution");
    if (savedResolution) setVideoResolution(savedResolution);
    const savedFps = Number(localStorage.getItem("videoFps"));
    if (savedFps) setFps(savedFps);
    const savedDuration = Number(localStorage.getItem("videoDuration"));
    if (savedDuration) setVideoDuration(savedDuration);
    setAudioEnabled(localStorage.getItem("audioEnabled") === "true");
  }, [isOpen]);

  useEffect(() => {
    if (!videoSettings) return;

    if (videoSettings.videoResolution) {
      setVideoResolution(videoSettings.videoResolution);
    }
    if (videoSettings.fps) {
      setFps(Number(videoSettings.fps));
    }
    if (videoSettings.videoDuration) {
      setVideoDuration(Number(videoSettings.videoDuration));
    }
    if (videoSettings.audioEnabled != null) {
      setAudioEnabled(Boolean(videoSettings.audioEnabled));
    }
  }, [videoSettings]);

  const handleFavoriteToggle = () => {
    if (!loading && collection?._id) {
      dispatch(toggleToFavorite({ collectionId: collection._id }));
    }
  };

  const handlePlaylistToggle = () => {
    if (!isLoading && collection?._id) {
      setIsAddToPlaylistModalOpen(true);
    }
  };

  const isCollectionHidden =
    isCollectionHiddenLocally || collection?.isPublic === false;

  const showHideVipTagButton =
    canManageCollectionVisibility &&
    (hasVipTag(collection) || isVipTagHiddenLocally);

  const hideCollectionAction = () => {
    if (!collection?._id || hideCollectionPending) return;

    const willShowCollection = isCollectionHidden;
    const hide = !willShowCollection;

    dispatch(hideCollection({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsCollectionHiddenLocally(!isCollectionHidden);
        setConfirmationAction(null);
        toast.success(
          willShowCollection
            ? "Collection shown successfully"
            : "Collection hidden successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowCollection
              ? "Failed to show collection"
              : "Failed to hide collection"),
        );
      });
  };

  const hideCollectionVipAction = () => {
    if (!collection?._id || hideCollectionVipPending) return;

    const willShowVipTag = isVipTagHiddenLocally;
    const hide = !willShowVipTag;

    dispatch(hideCollectionVip({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsVipTagHiddenLocally(!isVipTagHiddenLocally);
        setConfirmationAction(null);
        toast.success(
          willShowVipTag
            ? "VIP tag shown successfully"
            : "Collection hidden from VIP category successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowVipTag
              ? "Failed to show VIP tag"
              : "Failed to hide collection from VIP"),
        );
      });
  };

  const handleConfirmVisibilityAction = () => {
    if (confirmationAction === "hide") {
      hideCollectionAction();
      return;
    }
    if (confirmationAction === "hideVip") {
      hideCollectionVipAction();
    }
  };

  const openGalleryForTarget = (target) => {
    if (isLoading) return;
    setGalleryTarget(target);
    setIsUserGalleryModalOpen(true);
  };

  const createGalleryImageSelection = (imageItem) => ({
    file: null,
    url: imageItem.url,
    sourceUrl: imageItem.url,
  });

  const prepareImageForCrop = async (image, target) => {
    if (image?.file || !image?.sourceUrl || image?.url?.startsWith("blob:")) {
      return image;
    }

    try {
      const response = await fetch(image.sourceUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to load gallery image");

      const blob = await response.blob();
      const preparedImage = {
        ...image,
        url: URL.createObjectURL(blob),
      };

      if (target === 2) {
        setUploadedImage2(preparedImage);
      } else {
        setUploadedImage(preparedImage);
      }

      return preparedImage;
    } catch (error) {
      console.error("Failed to prepare gallery image for crop", error);
      return image;
    }
  };

  const openCropModalForTarget = async (target) => {
    const image = target === 2 ? uploadedImage2 : uploadedImage;
    if (!image) return;

    await prepareImageForCrop(image, target);
    setCropTarget(target);
    setIsOpenCropModal(true);
  };

  const handleGallerySelection = async (mediaItems) => {
    const imageItem = mediaItems.find((item) => item.type !== "video");
    if (!imageItem) {
      toast.error("Please select an image");
      return false;
    }

    const selected = createGalleryImageSelection(imageItem);
    if (galleryTarget === 2) {
      setUploadedImage2(selected);
    } else {
      setUploadedImage(selected);
    }
    setIsUserGalleryModalOpen(false);
    setGalleryTarget(null);
    return true;
  };

  const validateForm = () => {
    if (!uploadedImage || !uploadedImage2) {
      toast.error("Please add both input images");
      return false;
    }
    return true;
  };

  const getBlendImagePayload = (image, index) => {
    if (image?.file) {
      return index === 2 ? { image2: image.file } : { image: image.file };
    }

    const imageUrl = image?.sourceUrl || image?.url;

    return index === 2 ? { imageUrl2: imageUrl } : { imageUrl };
  };

  const resetStates = () => {
    setUploadedImage(null);
    setUploadedImage2(null);
    setUpscaleEnabled(false);
    setCustomPrompt("");
    setNegativePrompt("");
    setAudioPrompt("");
    setAudioEnabled(false);
    setShowPrompts(false);
  };

  const handleCancel = () => {
    if (!isLoading) {
      resetStates();
      onClose();
    }
  };

  const handleGenerationStarted = () => {
    toast.custom(() => (
      <GenerationToast
        successMessage="Generation started..."
        label="View"
        onClick={() => {
          navigate("/collection");
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }}
      />
    ));
  };

  const onGenerateClick = () => {
    if (!validateForm()) return;

    dispatch(
      generateBlendCollection({
        ...getBlendImagePayload(uploadedImage, 1),
        ...getBlendImagePayload(uploadedImage2, 2),
        customPrompt,
        audioCustomPrompt: audioPrompt,
        audio: audioEnabled,
        negativePrompt,
        upscale: upscaleEnabled,
        modelId: collection.model,
        resolution: videoResolution,
        seed: customSeed,
        fps,
        videoDuration,
        playlistId,
        ...(fromTrainingSelectCoverPage && {
          trainModel: collection.trainModel,
        }),
      }),
    )
      .unwrap()
      .then(() => {
        handleGenerationStarted();
        setIsExploding(true);

        setTimeout(() => {
          setIsExploding(false);

          if (uploadedImage && uploadedImageRef.current) {
            setFlyAnimation({
              isActive: true,
              imageUrl: uploadedImage.url,
              startPosition: uploadedImageRef.current.getBoundingClientRect(),
              imageDimensions: {
                width: uploadedImageRef.current.offsetWidth,
                height: uploadedImageRef.current.offsetHeight,
              },
              onComplete: () => {
                const alreadyGeneratedVideo = localStorage.getItem(
                  "alreadyGeneratedVideo",
                );
                if (!alreadyGeneratedVideo) {
                  localStorage.setItem("alreadyGeneratedVideo", true);
                  navigate("/collection");
                  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
                }
                setFlyAnimation({
                  isActive: false,
                  imageUrl: null,
                  startPosition: null,
                  imageDimensions: null,
                  onComplete: null,
                });
              },
            });
            handleCancel();
          } else {
            handleCancel();
          }
        }, 500);
      })
      .catch((err) => {
        toast.error(
          err ?? "An error occurred while generating. Please try again later.",
        );
      });
  };

  if (!user || !collection) return null;

  const generatePrice =
    (collection?.price ?? 0) +
    (upscaleEnabled ? UPSCALE_PRICE : 0) +
    (audioEnabled ? AUDIO_PRICE : 0) +
    (VIDEO_RESOLUTION_PRICE[videoResolution] ?? 0) +
    (VIDEO_DURATION_PRICE[videoDuration] ?? 0) +
    (VIDEO_FPS_PRICE[fps] ?? 0);

  const confirmationTitle =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Show VIP Tag"
        : "Hide VIP Tag"
      : isCollectionHidden
        ? "Show Collection"
        : "Hide Collection";
  const confirmationMessage =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Are you sure you want to show this collection in the VIP category?"
        : "Are you sure you want to remove this collection from the VIP category?"
      : isCollectionHidden
        ? "Are you sure you want to show this collection?"
        : "Are you sure you want to hide this collection?";
  const confirmationLoading =
    confirmationAction === "hideVip"
      ? hideCollectionVipPending
      : hideCollectionPending;

  const renderGenerateContainer = () => (
    <>
      <StyledIconWrapper
        onClick={() => !isLoading && setIsEnterPromptModalOpen(true)}
      >
        <LottieIcon
          animationData={settingsAnimation}
          trigger="hover"
          strokeColor={"#fff"}
          size={28}
        />
      </StyledIconWrapper>
      <ExplosionButton
        style={{ margin: "0px 6px", flex: 1, width: "100%" }}
        disabled={generateLoading}
        onClick={onGenerateClick}
        isLoading={generateLoading}
        isExploding={isExploding}
        small
      >
        {generateLoading ? (
          <l-line-spinner size="16" stroke="1.25" speed="1.5" color="black" />
        ) : (
          <>
            Generate
            <GradientStarIcon />
            {Math.ceil(generatePrice)}
          </>
        )}
      </ExplosionButton>
    </>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!isLoading) {
            resetStates();
            onClose();
          }
        }}
      >
        <ModalContainer>
          <Header>
            <Title>{formatModelName(collection.name)}</Title>
            {fromTrainingSelectCoverPage ? (
              <IconWrapper
                onClick={() => {
                  if (!isLoading) {
                    resetStates();
                    onClose();
                  }
                }}
              >
                <CloseIcon />
              </IconWrapper>
            ) : (
              <RowIcons>
                <IconWrapper
                  onClick={() => {
                    onClose();
                    navigate(`/template/${collection._id}`, {
                      state: { collection },
                    });
                    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
                  }}
                >
                  <CollectionDetailsIcon />
                </IconWrapper>

                {!collection?.isDisturbing && !collection?.isGay && (
                  <IconWrapper onClick={handlePlaylistToggle}>
                    <PlaylistIcon />
                  </IconWrapper>
                )}

                <IconWrapper style={{ zIndex: 1 }}>
                  <LottieIcon
                    animationData={likeAnimation}
                    trigger="toggle"
                    isToggled={user.favoriteCollections?.includes(
                      collection._id,
                    )}
                    onToggle={handleFavoriteToggle}
                    likeSegment={[1, 90]}
                    unlikeSegment={[90, 180]}
                    size={50}
                    absolute
                  />
                </IconWrapper>

                <IconWrapper
                  onClick={() => {
                    if (!isLoading) {
                      resetStates();
                      onClose();
                    }
                  }}
                >
                  <CloseIcon />
                </IconWrapper>
              </RowIcons>
            )}
          </Header>

          <CategoryFadeWrapper $faded={isCategoryFaded}>
            <CategoryContainer ref={categoryRef}>
              {!isOnCollectionUserProfile && (
                <CategoryItem
                  $click
                  onClick={() => {
                    navigate(`/profile/${collection?.username}`);
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                  }}
                >
                  <CategoryText>@{collection?.username}</CategoryText>
                </CategoryItem>
              )}
              {!!collection.keywords &&
                collection.keywords.map((keyword, index) => (
                  <CategoryItem key={index}>
                    <CategoryText>{keyword}</CategoryText>
                  </CategoryItem>
                ))}
            </CategoryContainer>
          </CategoryFadeWrapper>

          {!fromTrainingSelectCoverPage && canManageCollectionVisibility && (
            <AdminActionsContainer>
              <AdminActionButton
                onClick={() => setConfirmationAction("hide")}
                disabled={hideCollectionPending}
              >
                {hideCollectionPending
                  ? "Saving..."
                  : isCollectionHidden
                    ? "Show"
                    : "Hide"}
              </AdminActionButton>

              {showHideVipTagButton && (
                <AdminActionButton
                  onClick={() => setConfirmationAction("hideVip")}
                  disabled={hideCollectionVipPending}
                >
                  {hideCollectionVipPending
                    ? "Saving..."
                    : isVipTagHiddenLocally
                      ? "Show VIP tag"
                      : "Hide VIP tag"}
                </AdminActionButton>
              )}
            </AdminActionsContainer>
          )}

          <Content>
            <Container>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <ImageContainer
                  style={{
                    width: "100%",
                    maxWidth: "unset",
                    height: 192,
                    maxHeight: 192,
                  }}
                >
                  {uploadedImage && (
                    <DeleteButton
                      $compact
                      onClick={() => setUploadedImage(null)}
                    >
                      <LottieIcon
                        animationData={deleteAnimation}
                        trigger="hover"
                        size={18}
                        strokeColor={"#fff"}
                      />
                    </DeleteButton>
                  )}
                  {uploadedImage && (
                    <CropButton
                      $compact
                      onClick={() => openCropModalForTarget(1)}
                    >
                      <CropIcon />
                    </CropButton>
                  )}
                  <Image
                    src={
                      uploadedImage?.url ||
                      (collection?.input?.image?.url ?? input1Placeholder)
                    }
                    alt="Group input image 1"
                    ref={uploadedImageRef}
                    $hideImageContainer={!uploadedImage?.url}
                  />
                  <UploadActions>
                    <UploadAdButton />
                    <SelectImageButton
                      onClick={() => openGalleryForTarget(1)}
                      disabled={isLoading}
                      style={{ width: "100%" }}
                    />
                  </UploadActions>
                </ImageContainer>

                <ImageContainer
                  style={{
                    width: "100%",
                    maxWidth: "unset",
                    height: 192,
                    maxHeight: 192,
                  }}
                >
                  {uploadedImage2 && (
                    <DeleteButton
                      $compact
                      onClick={() => setUploadedImage2(null)}
                    >
                      <LottieIcon
                        animationData={deleteAnimation}
                        trigger="hover"
                        size={18}
                        strokeColor={"#fff"}
                      />
                    </DeleteButton>
                  )}
                  {uploadedImage2 && (
                    <CropButton
                      $compact
                      onClick={() => openCropModalForTarget(2)}
                    >
                      <CropIcon />
                    </CropButton>
                  )}
                  <Image
                    src={
                      uploadedImage2?.url ||
                      (collection?.input?.image?.resizedImage?.url ??
                        input2Placeholder)
                    }
                    alt="Group input image 2"
                    $hideImageContainer={!uploadedImage2?.url}
                  />
                  <UploadActions>
                    <SelectImageButton
                      onClick={() => openGalleryForTarget(2)}
                      disabled={isLoading}
                      style={{ width: "100%" }}
                    />
                  </UploadActions>
                </ImageContainer>
              </div>

              {collection?.output?.video?.compressedUrl ||
              collection?.output?.video?.url ||
              collection?.input.image?.resizedImage?.url ||
              collection?.input.image?.url ? (
                <MemoizedRightContent
                  output={collection?.output}
                  input={collection?.input}
                />
              ) : (
                <RightContent>
                  <StyledVideo
                    autoplay
                    src={groupVideoPlaceholder}
                    imageSrc={input1Placeholder}
                  />
                  <RightContentOverlay />
                </RightContent>
              )}
            </Container>
          </Content>

          <SettingsContainer style={{ padding: "0 16px" }}>
            <NudifyContainer
              onClick={() => {
                if (!isLoading) setUpscaleEnabled(!upscaleEnabled);
              }}
            >
              <LottieIcon
                animationData={checkboxAnimation}
                speed={2}
                trigger="toggle-reverse"
                isActive={upscaleEnabled}
                onActiveChange={() => {
                  if (!isLoading) setUpscaleEnabled(!upscaleEnabled);
                }}
                onClick={() => {
                  if (!isLoading) setUpscaleEnabled(!upscaleEnabled);
                }}
                size={24}
                strokeColor={!upscaleEnabled ? "#2877d9" : "#ffffff"}
              />
              <NudifyText>Enhance Photo</NudifyText>
            </NudifyContainer>
          </SettingsContainer>

          <GenerateContainer style={{ padding: "0 16px" }}>
            {renderGenerateContainer()}
          </GenerateContainer>
          <GenerateContainerMobile>
            {renderGenerateContainer()}
          </GenerateContainerMobile>
        </ModalContainer>
      </Modal>

      <CreateImageModal
        isOpen={isCreateImageFromGalleryOpen}
        onClose={() => setIsCreateImageFromGalleryOpen(false)}
        overlayZIndex={5000}
      />
      <UserGalleryModal
        isOpen={isUserGalleryModalOpen}
        onCreateImage={() => setIsCreateImageFromGalleryOpen(true)}
        onClose={() => {
          setIsUserGalleryModalOpen(false);
          setGalleryTarget(null);
        }}
        onConfirm={handleGallerySelection}
        maxSelectable={1}
        confirmLabel="Select"
        showPrice={false}
        showUploadTile
        allowedMediaTypes={["image"]}
        overlayZIndex={3600}
      />

      <CropModal
        selectedImage={cropTarget === 2 ? uploadedImage2 : uploadedImage}
        onClose={() => {
          setIsOpenCropModal(false);
          setCropTarget(null);
        }}
        onConfirm={(croppedImageData) => {
          const cropped = {
            file: new File(
              [croppedImageData.blob],
              `cropped-${Date.now()}.jpg`,
              { type: "image/jpeg" },
            ),
            url: croppedImageData.url,
            blob: croppedImageData.blob,
          };
          if (cropTarget === 2) setUploadedImage2(cropped);
          else setUploadedImage(cropped);
          setCropTarget(null);
        }}
        isOpen={isOpenCropModal}
      />

      <ConfirmationModal
        isOpen={!!confirmationAction}
        onClose={() => setConfirmationAction(null)}
        onConfirm={handleConfirmVisibilityAction}
        title={confirmationTitle}
        message={confirmationMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={confirmationLoading}
      />

      <AddToPlaylistModal
        isOpen={isAddToPlaylistModalOpen}
        isCreatingNew={isCreatingNew}
        setIsCreatingNew={setIsCreatingNew}
        openCreatePlaylistModal={() => setIsCreatePlaylistModalOpen(true)}
        onClose={() => setIsAddToPlaylistModalOpen(false)}
        collectionId={collection?._id}
      />
      <CreatePlaylistModal
        playlistName={playlistName}
        setPlaylistName={setPlaylistName}
        isOpen={isCreatePlaylistModalOpen}
        onClose={() => setIsCreatePlaylistModalOpen(false)}
      />

      <EnterPromptModal
        isOpen={isEnterPromptModalOpen}
        onClose={() => setIsEnterPromptModalOpen(false)}
        overlayZIndex={3700}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        audioPrompt={audioPrompt}
        setAudioPrompt={setAudioPrompt}
        audioEnabled={audioEnabled}
        setAudioEnabled={setAudioEnabled}
        videoResolution={videoResolution}
        setVideoResolution={setVideoResolution}
        enableVideoQuality={
          collection?.modelName ? !isImageOutput(collection.modelName) : false
        }
        modelPrice={
          (collection?.price ?? 0) + (upscaleEnabled ? UPSCALE_PRICE : 0)
        }
        customSeed={customSeed}
        setCustomSeed={setCustomSeed}
        collectionSeed={collection?.seed}
        fps={fps}
        setFps={setFps}
        videoDuration={videoDuration}
        setVideoDuration={setVideoDuration}
        showPrompts={showPrompts}
        setShowPrompts={setShowPrompts}
        resetSeed={isOpen}
        isMinVideoLength8sec={collection?.output?.videoDuration === 8}
        collectionId={collection?._id}
        hideAudio={fromTrainingSelectCoverPage}
      />

      {flyAnimation.isActive && (
        <FlyToCollection
          isActive={flyAnimation.isActive}
          imageUrl={flyAnimation.imageUrl}
          startPosition={flyAnimation.startPosition}
          imageDimensions={flyAnimation.imageDimensions}
          onComplete={() => {
            dispatch(triggerCollectionTabAnimation());
            if (flyAnimation.onComplete) flyAnimation.onComplete();
            setFlyAnimation({
              isActive: false,
              imageUrl: null,
              startPosition: null,
              imageDimensions: null,
              onComplete: null,
            });
          }}
        />
      )}
    </>
  );
};

export default GroupModal;
